import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { Flash } from "~/jose_modern/flash.js";
import { useWarnUnsavedChanges } from "../use-warn-unsaved-changes.js";
import { GENERIC_ERROR } from "./constants.js";

export function useLineItemRow(props, emit, options = {}) {
  const {
    attributes = (base) => base,
    identity = () => ({}),
    canSave = () => true,
    focusItem = () => {},
    focusQuantity = () => {},
  } = options;

  const runMutation = inject("runMutation", (fn) => fn());

  const labels = computed(() => ({
    model: "Item",
    removeTitle: "Remove Item",
    removeMessage: "Are you sure you want to remove this item?",
    ...props.labels,
  }));

  const itemId = ref(props.item?.item_id || null);
  const name = ref(props.item?.item_name || "");
  const itemCustomName = ref(props.item?.item_custom_name || "");
  const quantity = ref(props.item?.quantity || "");
  const unit = ref(props.item?.unit || "");
  const units = ref(props.item?.units || []);
  const id = ref(props.item?.id || null);
  const saving = ref(false);
  const errored = ref(false);
  const showConfirmRemove = ref(false);
  const dirty = ref(false);
  const rowEl = ref(null);

  const displayName = computed(() => itemCustomName.value || name.value);
  const isDraft = computed(() => !id.value);

  const quantityError = computed(() => {
    if (quantity.value !== "" && parseFloat(quantity.value) <= 0)
      return "Quantity must be positive";
    return null;
  });

  const excludedItemIds = computed(() =>
    props.excludedItemIds.filter((excludedId) => excludedId !== itemId.value)
  );

  const itemAttributes = () =>
    attributes({ item_id: itemId.value, quantity: quantity.value, unit: unit.value });

  const itemIdentity = () =>
    itemId.value == null
      ? null
      : { item_id: itemId.value, ...identity({ quantity: quantity.value, unit: unit.value }) };

  // A rejected fetch (offline, aborted) never reaches the response check, so it is
  // funneled into the same errored-row treatment as a rejection from the server.
  const failedRequest = () => ({ response: null, data: null });

  function onError(data) {
    const errorMessage = data?.error || data?.errors?.join(", ");
    Flash.error(errorMessage || GENERIC_ERROR);
    errored.value = true;
  }

  useWarnUnsavedChanges(() => dirty.value || saving.value || errored.value);

  watch(
    () => dirty.value || saving.value,
    (isDirty) => emit("dirty-changed", isDirty)
  );

  onUnmounted(() => {
    emit("dirty-changed", false);
  });

  onMounted(() => {
    if (isDraft.value && itemId.value) dirty.value = true;
    if (props.autofocus && !itemId.value) nextTick(focusItem);
  });

  function selectItem(result) {
    const selection = props.mapSelection(result);
    itemId.value = selection.itemId;
    name.value = selection.name;
    itemCustomName.value = selection.customName || "";
    units.value = selection.units;
    unit.value = selection.unit;
    nextTick(focusQuantity);
  }

  function clearSelection({ keepQuantity = false } = {}) {
    itemId.value = null;
    name.value = "";
    itemCustomName.value = "";
    if (!keepQuantity) quantity.value = "";
    unit.value = "";
    units.value = [];

    nextTick(focusItem);
  }

  watch(itemAttributes, () => {
    dirty.value = true;
  });

  watch(itemIdentity, (item) => emit("item-changed", item));

  function onRowFocusOut(event) {
    if (rowEl.value?.contains(event.relatedTarget)) return;
    onFieldChange();
  }

  function onEnter(event) {
    event.target.blur();
    onFieldChange();
  }

  const withSaving = async (fn) => {
    saving.value = true;
    try {
      await fn();
    } finally {
      saving.value = false;
    }
  };

  const readyToSave = () =>
    !saving.value &&
    dirty.value &&
    itemId.value &&
    quantity.value &&
    quantity.value > 0 &&
    canSave();

  async function onFieldChange() {
    if (!readyToSave()) return;

    await withSaving(isDraft.value ? createItem : updateItem);

    // If we changed the values while saving, we save again afterwards
    if (dirty.value && !errored.value) nextTick(onFieldChange);
  }

  async function request(apiFn, onSuccess) {
    await runMutation(async () => {
      const previousState = JSON.stringify(itemAttributes());
      const { response, data } = await apiFn().catch(failedRequest);

      if (response?.ok) {
        errored.value = false;
        dirty.value = previousState !== JSON.stringify(itemAttributes());
        onSuccess(data);
      } else {
        onError(data);
      }
    });
  }

  const createItem = () =>
    request(
      () => props.api.create(itemAttributes()),
      (data) => {
        id.value = data.id;
        emit("created", data);
        Flash.modelCreated(labels.value.model, true);
      }
    );

  const updateItem = () =>
    request(
      () => props.api.update(id.value, itemAttributes()),
      () => {
        Flash.modelUpdated(labels.value.model, true);
      }
    );

  function removeItem() {
    if (isDraft.value) {
      emit("remove", { wasSaved: false });
      return;
    }

    showConfirmRemove.value = true;
  }

  async function confirmRemove() {
    await withSaving(() =>
      runMutation(async () => {
        const { response, data } = await props.api.remove(id.value).catch(failedRequest);

        if (response?.ok) {
          Flash.modelRemoved(labels.value.model, true);
          emit("remove", { wasSaved: true });
        } else {
          onError(data);
        }
      })
    );
  }

  return {
    labels,
    itemId,
    name,
    itemCustomName,
    displayName,
    quantity,
    unit,
    units,
    id,
    saving,
    errored,
    showConfirmRemove,
    dirty,
    rowEl,
    isDraft,
    quantityError,
    excludedItemIds,
    itemAttributes,
    selectItem,
    clearSelection,
    onRowFocusOut,
    onEnter,
    onFieldChange,
    removeItem,
    confirmRemove,
  };
}
